import { Comment, CreateCommentData, UpdateCommentData } from "../types/comment";
import { supabase } from "../lib/supabase";

export const commentService = {
  async getCommentsBySection(sectionId: string, scope: 'own' | 'all' = 'own'): Promise<Comment[]> {
    try {
      // Get current user for scoping
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.warn('User not authenticated, returning empty comments');
        return [];
      }

      let query = supabase
        .from('comments')
        .select(`
          id,
          user_id,
          block_id,
          content,
          created_at,
          updated_at,
          user_profiles!inner (
            first_name,
            last_name,
            id
          )
        `)
        .like('block_id', `${sectionId}-block-%`)
        .order('created_at', { ascending: true });

      // For reading UI, explicitly filter to current user's comments only
      // This ensures even admins only see their own comments in the reading interface
      if (scope === 'own') {
        query = query.eq('user_id', user.id);
      }
      // For admin UI, rely on RLS admin policy to return all comments

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching comments by section:', error);
        throw new Error(`Failed to fetch comments: ${error.message}`);
      }

      return (data || []).map(comment => ({
        id: comment.id,
        userId: comment.user_id,
        blockId: comment.block_id,
        content: comment.content,
        createdAt: comment.created_at,
        updatedAt: comment.updated_at,
        user: {
          firstName: (comment.user_profiles as any).first_name,
          lastName: (comment.user_profiles as any).last_name,
          email: '' // Email not included for privacy
        }
      }));
    } catch (error) {
      console.error('Error in getCommentsBySection:', error);
      throw error;
    }
  },

  async getAllComments(): Promise<Comment[]> {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          id,
          user_id,
          block_id,
          content,
          created_at,
          updated_at,
          user_profiles!inner (
            first_name,
            last_name,
            id
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching all comments:', error);
        throw new Error(`Failed to fetch comments: ${error.message}`);
      }

      return (data || []).map(comment => ({
        id: comment.id,
        userId: comment.user_id,
        blockId: comment.block_id,
        content: comment.content,
        createdAt: comment.created_at,
        updatedAt: comment.updated_at,
        user: {
          firstName: (comment.user_profiles as any).first_name,
          lastName: (comment.user_profiles as any).last_name,
          email: '' // Email not included for privacy
        }
      }));
    } catch (error) {
      console.error('Error in getAllComments:', error);
      throw error;
    }
  },

  async createComment(data: CreateCommentData): Promise<Comment> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User must be authenticated to create comments');
      }

      // Insert comment
      const { data: commentData, error: insertError } = await supabase
        .from('comments')
        .insert({
          user_id: user.id,
          block_id: data.blockId,
          content: data.content
        })
        .select(`
          id,
          user_id,
          block_id,
          content,
          created_at,
          updated_at,
          user_profiles!inner (
            first_name,
            last_name,
            id
          )
        `)
        .single();

      if (insertError) {
        console.error('Error creating comment:', insertError);
        throw new Error(`Failed to create comment: ${insertError.message}`);
      }

      return {
        id: commentData.id,
        userId: commentData.user_id,
        blockId: commentData.block_id,
        content: commentData.content,
        createdAt: commentData.created_at,
        updatedAt: commentData.updated_at,
        user: {
          firstName: (commentData.user_profiles as any).first_name,
          lastName: (commentData.user_profiles as any).last_name,
          email: '' // Email not included for privacy
        }
      };
    } catch (error) {
      console.error('Error in createComment:', error);
      throw error;
    }
  },

  async updateComment(data: UpdateCommentData): Promise<Comment> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User must be authenticated to update comments');
      }

      // Update comment (RLS will ensure user can only update their own comments)
      const { data: commentData, error: updateError } = await supabase
        .from('comments')
        .update({
          content: data.content,
          updated_at: new Date().toISOString()
        })
        .eq('id', data.id)
        .eq('user_id', user.id) // Extra security check
        .select(`
          id,
          user_id,
          block_id,
          content,
          created_at,
          updated_at,
          user_profiles!inner (
            first_name,
            last_name,
            id
          )
        `)
        .single();

      if (updateError) {
        console.error('Error updating comment:', updateError);
        throw new Error(`Failed to update comment: ${updateError.message}`);
      }

      return {
        id: commentData.id,
        userId: commentData.user_id,
        blockId: commentData.block_id,
        content: commentData.content,
        createdAt: commentData.created_at,
        updatedAt: commentData.updated_at,
        user: {
          firstName: (commentData.user_profiles as any).first_name,
          lastName: (commentData.user_profiles as any).last_name,
          email: '' // Email not included for privacy
        }
      };
    } catch (error) {
      console.error('Error in updateComment:', error);
      throw error;
    }
  },

  async deleteComment(commentId: string): Promise<void> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User must be authenticated to delete comments');
      }

      // Delete comment (RLS will ensure user can only delete their own comments)
      const { error: deleteError } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id); // Extra security check

      if (deleteError) {
        console.error('Error deleting comment:', deleteError);
        throw new Error(`Failed to delete comment: ${deleteError.message}`);
      }
    } catch (error) {
      console.error('Error in deleteComment:', error);
      throw error;
    }
  },

  async getCommentStats() {
    try {
      // Get total comments
      const { count: totalComments, error: totalError } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true });

      if (totalError) {
        console.error('Error getting total comments:', totalError);
      }

      // Get unique users count
      const { data: uniqueUsersData, error: usersError } = await supabase
        .from('comments')
        .select('user_id')
        .neq('user_id', null);

      const uniqueCommenters = uniqueUsersData 
        ? new Set(uniqueUsersData.map(c => c.user_id)).size 
        : 0;

      if (usersError) {
        console.error('Error getting unique users:', usersError);
      }

      // Get comments from last 24 hours
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      const { count: commentsToday, error: todayError } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneDayAgo.toISOString());

      if (todayError) {
        console.error('Error getting today comments:', todayError);
      }

      // Get comments from last 7 days
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const { count: commentsThisWeek, error: weekError } = await supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', oneWeekAgo.toISOString());

      if (weekError) {
        console.error('Error getting week comments:', weekError);
      }

      return {
        totalComments: totalComments || 0,
        totalUsers: uniqueCommenters,
        recentComments: commentsToday || 0,
        commentsToday: commentsToday || 0,
        commentsThisWeek: commentsThisWeek || 0,
        uniqueCommenters
      };
    } catch (error) {
      console.error('Error in getCommentStats:', error);
      // Return default stats on error
      return {
        totalComments: 0,
        totalUsers: 0,
        recentComments: 0,
        commentsToday: 0,
        commentsThisWeek: 0,
        uniqueCommenters: 0
      };
    }
  }
};

export default commentService;
